import React, { useState } from "react";
import classNames from "classnames";

import { getStatusValues } from "../model/calculations";
import { getMappingLabel, mappingLabels } from "../model/defaults";
import {
  buildTargetsTemplate,
  getDistinctMarketValues,
  parseTargetsTsv,
} from "../model/targets";
import {
  MappingKey,
  ReportConfiguration,
  ReportingField,
  ReportingRecord,
  StatusMode,
  AdditionalDimension,
  CreatorFallbackRule,
} from "../model/types";
import FieldSelect from "./FieldSelect";

interface ConfigurationSectionProps {
  configuration: ReportConfiguration;
  fields: ReportingField[];
  records: ReportingRecord[];
  /** True when the local configuration differs from what the workspace has stored. */
  isDirty: boolean;
  isStored: boolean;
  savedAt: string | null;
  isSaving: boolean;
  canSave: boolean;
  onChange: (patch: Partial<ReportConfiguration>) => void;
  onMappingChange: (key: MappingKey, uuid: string) => void;
  onSave: () => void;
  onReset: () => void;
}

/**
 * Section 1 - admin owned.
 *
 * Maps workspace fields onto the report definition and decides which records are
 * eligible. Everything here feeds `source.fields` and `eligibility` in the generated
 * configuration. Nothing here reads or writes the end-user `ReportView`.
 *
 * Changes are held locally until the user stores them: the mapping decides what every
 * viewer of this workspace sees, so it should be checked against the preview first. Reset
 * is likewise local - it restores the last stored state (or the defaults) in the form and
 * only reaches the database if the user then stores it.
 */
export default function ConfigurationSection(props: ConfigurationSectionProps) {
  const {
    configuration,
    fields,
    records,
    isDirty,
    isStored,
    savedAt,
    isSaving,
    canSave,
    onChange,
    onMappingChange,
    onSave,
    onReset,
  } = props;

  // Collapsed by default: the mapping is set once and then left alone, so it should not
  // push the end-user controls and the preview down the page on every visit.
  //
  // Collapse state is presentational and belongs to this section alone, so it stays local
  // rather than widening the module's props surface.
  const [isExpanded, setIsExpanded] = useState(false);

  const statusValues = getStatusValues(records, configuration);

  const unmapped = (Object.keys(mappingLabels) as MappingKey[]).filter(
    (role) => !configuration.mappings[role],
  );
  const managerLabel = getMappingLabel(configuration.roleLabels, "manager");
  const marketLabel = getMappingLabel(configuration.roleLabels, "market");
  const creatorNameLabel = getMappingLabel(
    configuration.roleLabels,
    "creatorName",
  );

  const toggleStatus = (status: string) => {
    const included = configuration.includedStatuses.includes(status)
      ? configuration.includedStatuses.filter((value) => value !== status)
      : [...configuration.includedStatuses, status];
    onChange({ includedStatuses: included });
  };

  const updateAdditionalDimension = (
    index: number,
    patch: Partial<AdditionalDimension>,
  ) => {
    const key = `customDimension${index + 1}` as AdditionalDimension["key"];
    const current = configuration.additionalDimensions.find(
      (dimension) => dimension.key === key,
    ) ?? {
      key,
      label: "",
      fieldUuids: [],
    };
    const dimensions = configuration.additionalDimensions.filter(
      (dimension) => dimension.key !== key,
    );
    dimensions.push({ ...current, ...patch });
    onChange({ additionalDimensions: dimensions });
  };

  const updateCreatorFallbackRule = (
    index: number,
    patch: Partial<CreatorFallbackRule>,
  ) => {
    onChange({
      creatorFallbackRules: configuration.creatorFallbackRules.map(
        (rule, ruleIndex) =>
          ruleIndex === index ? { ...rule, ...patch } : rule,
      ),
    });
  };

  const addCreatorFallbackRule = () => {
    onChange({
      creatorFallbackRules: [
        ...configuration.creatorFallbackRules,
        { costTypeValue: "", label: "" },
      ],
    });
  };

  const removeCreatorFallbackRule = (index: number) => {
    onChange({
      creatorFallbackRules: configuration.creatorFallbackRules.filter(
        (_, ruleIndex) => ruleIndex !== index,
      ),
    });
  };

  const [targetsYear, setTargetsYear] = useState(() =>
    new Date().getFullYear(),
  );
  const [targetsTsv, setTargetsTsv] = useState("");
  const [targetsImportErrors, setTargetsImportErrors] = useState<string[]>([]);

  const copyTargetsTemplate = async () => {
    const markets = getDistinctMarketValues(records, configuration);
    const template = buildTargetsTemplate(markets, targetsYear);
    setTargetsTsv(template);

    try {
      await navigator.clipboard.writeText(template);
    } catch {
      // Clipboard access can be denied by the browser; the textarea below has it either way.
    }
  };

  const importTargetsTsv = () => {
    const { targets, errors } = parseTargetsTsv(targetsTsv);
    setTargetsImportErrors(errors);
    if (targets.length) onChange({ targets });
  };

  const targetMarketCount = new Set(
    configuration.targets.map((target) => target.market),
  ).size;

  const memberFields = fields.filter((field) => field.type === "member");

  const renderSavedState = () => {
    if (isDirty) return "Unsaved changes";
    if (!isStored) return "Using defaults - not stored yet";
    if (savedAt) {
      return `Stored ${new Date(savedAt).toLocaleString("en-GB")}`;
    }
    return "Stored";
  };

  return (
    <section
      className={classNames(
        "finance-reporting-group",
        "finance-reporting-group--config",
        { "finance-reporting-group--collapsed": !isExpanded },
      )}
      aria-labelledby="finance-reporting-config-title"
    >
      <header className="finance-reporting-group__heading">
        <h3
          className="finance-reporting-group__title"
          id="finance-reporting-config-title"
        >
          <button
            type="button"
            className="finance-reporting-group__toggle"
            aria-expanded={isExpanded}
            aria-controls="finance-reporting-config-body"
            onClick={() => setIsExpanded((expanded) => !expanded)}
          >
            <span className="finance-reporting-group__toggle-text">
              <span className="finance-reporting-group__title-text">
                Configuration
              </span>
            </span>
            {/* The callout and the Store button live in the collapsed body, so anything
                needing action has to be advertised on the header itself. */}
            {unmapped.length > 0 && (
              <span className="finance-reporting-group__badge finance-reporting-group__badge--warning">
                {unmapped.length} field{unmapped.length === 1 ? "" : "s"} to map
              </span>
            )}
            {isDirty && (
              <span className="finance-reporting-group__badge">
                Unsaved changes
              </span>
            )}
            <svg
              className="finance-reporting-group__chevron"
              viewBox="0 0 24 24"
              width="18"
              height="18"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M7 10l5 5 5-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </h3>
        {isExpanded && (
          <p className="finance-reporting-group__note">
            Maps workspace fields onto the report definition and decides which
            records are eligible. Stored on the workspace and shared by
            everyone.
          </p>
        )}
      </header>

      {isExpanded && (
        <div id="finance-reporting-config-body">
          {unmapped.length > 0 && (
            <div className="finance-reporting-callout">
              <strong>Fields to map</strong>
              <span>
                This workspace has no field matching the default for{" "}
                {unmapped
                  .map((role) =>
                    getMappingLabel(configuration.roleLabels, role),
                  )
                  .join(", ")}
                . Pick one below, then store the configuration.
              </span>
            </div>
          )}

          <div className="finance-reporting-section">
            <div className="finance-reporting-section__heading">
              <span className="finance-reporting-section__number">01</span>
              <div>
                <h4>Report details</h4>
                <p>Used to identify the saved report.</p>
              </div>
            </div>
            <label className="finance-reporting-field">
              <span className="finance-reporting-field__label">
                Report name
              </span>
              <input
                className="finance-reporting-field__control"
                type="text"
                value={configuration.reportName}
                onChange={(event) =>
                  onChange({ reportName: event.target.value })
                }
              />
            </label>
            <label className="finance-reporting-field">
              <span className="finance-reporting-field__label">
                Description
              </span>
              <textarea
                className="finance-reporting-field__control"
                rows={2}
                value={configuration.description}
                onChange={(event) =>
                  onChange({ description: event.target.value })
                }
              />
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginTop: 12,
              }}
            >
              <input
                type="checkbox"
                checked={configuration.disableFrontendRefreshAction}
                onChange={(event) =>
                  onChange({
                    disableFrontendRefreshAction: event.target.checked,
                  })
                }
              />
              <span>Disable the &quot;Refresh now&quot; button</span>
            </label>
            <p className="finance-reporting-field__hint">
              Turn on when this workspace&apos;s dataset is kept fresh by
              something other than this module (e.g. an external sync) -
              &quot;Refresh now&quot; would otherwise trigger this module&apos;s
              own rebuild and overwrite it.
            </p>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginTop: 12,
              }}
            >
              <input
                type="checkbox"
                checked={configuration.dataQualityEnabled}
                onChange={(event) =>
                  onChange({ dataQualityEnabled: event.target.checked })
                }
              />
              <span>Show the data completeness panel</span>
            </label>
            <p className="finance-reporting-field__hint">
              Turn off when every record in this workspace&apos;s dataset is
              already vetted and complete (e.g. a reconciled finance import) -
              the completeness score and contributing-record breakdown would
              otherwise be noise.
            </p>
          </div>

          <div className="finance-reporting-section">
            <div className="finance-reporting-section__heading">
              <span className="finance-reporting-section__number">02</span>
              <div>
                <h4>Financial fields</h4>
                <p>Numeric values used in KPI calculations.</p>
              </div>
            </div>
            <FieldSelect
              label="Revenue"
              fieldRole="measure"
              fields={fields}
              value={configuration.mappings.revenue}
              onChange={(uuid) => onMappingChange("revenue", uuid)}
            />
            <FieldSelect
              label="Creator cost"
              fieldRole="measure"
              fields={fields}
              value={configuration.mappings.cost}
              onChange={(uuid) => onMappingChange("cost", uuid)}
            />
            <div className="finance-reporting-note">
              <span>Calculated</span>
              <code>Profit = SUM(revenue) - SUM(cost)</code>
            </div>
          </div>

          <div className="finance-reporting-section">
            <div className="finance-reporting-section__heading">
              <span className="finance-reporting-section__number">03</span>
              <div>
                <h4>Reporting logic</h4>
                <p>Controls which records contribute to totals.</p>
              </div>
            </div>
            <FieldSelect
              label="Reporting date"
              fieldRole="date"
              fields={fields}
              value={configuration.mappings.date}
              onChange={(uuid) => onMappingChange("date", uuid)}
            />
            <FieldSelect
              label="Budget month fallback"
              fieldRole="budgetMonth"
              fields={fields}
              value={configuration.mappings.budgetMonth}
              onChange={(uuid) => onMappingChange("budgetMonth", uuid)}
            />
            <p className="finance-reporting-field__hint">
              Used only when Reporting date is empty. Expected format: YYYY/MM.
            </p>
            <label className="finance-reporting-field">
              <span className="finance-reporting-field__label">
                Earliest reporting month
              </span>
              <input
                className="finance-reporting-field__control"
                type="month"
                value={configuration.reportingStartMonth ?? ""}
                onChange={(event) =>
                  onChange({ reportingStartMonth: event.target.value || null })
                }
              />
            </label>
            <p className="finance-reporting-field__hint">
              Records allocated before this month are excluded from every total,
              filter and breakdown. Leave blank to include all dates.
            </p>
            <label className="finance-reporting-field">
              <span className="finance-reporting-field__label">
                Reporting date label
              </span>
              <input
                className="finance-reporting-field__control"
                type="text"
                value={configuration.allocationLabels.reportingDate}
                onChange={(event) =>
                  onChange({
                    allocationLabels: {
                      ...configuration.allocationLabels,
                      reportingDate: event.target.value,
                    },
                  })
                }
              />
            </label>
            <label className="finance-reporting-field">
              <span className="finance-reporting-field__label">
                Budget month label
              </span>
              <input
                className="finance-reporting-field__control"
                type="text"
                value={configuration.allocationLabels.budgetMonth}
                onChange={(event) =>
                  onChange({
                    allocationLabels: {
                      ...configuration.allocationLabels,
                      budgetMonth: event.target.value,
                    },
                  })
                }
              />
            </label>
            <p className="finance-reporting-field__hint">
              How the two allocation buckets are labelled throughout the report
              (charts, KPI cards, legends). Blank falls back to
              &quot;Actual&quot; / &quot;Forecast&quot;.
            </p>
            <FieldSelect
              label="Status field"
              fieldRole="dimension"
              fields={fields}
              value={configuration.mappings.status}
              onChange={(uuid) => onMappingChange("status", uuid)}
            />
            <label className="finance-reporting-field">
              <span className="finance-reporting-field__label">
                Status handling
              </span>
              <select
                className="finance-reporting-field__control"
                value={configuration.statusMode}
                onChange={(event) =>
                  onChange({ statusMode: event.target.value as StatusMode })
                }
              >
                <option value="all">Do not filter</option>
                <option value="selected">Include selected statuses</option>
              </select>
            </label>

            {configuration.statusMode === "selected" && (
              <fieldset className="finance-reporting-fieldset">
                <legend>Included statuses</legend>
                <div className="finance-reporting-chips">
                  {statusValues.length ? (
                    statusValues.map((status) => (
                      <label key={status} className="finance-reporting-chip">
                        <input
                          type="checkbox"
                          checked={configuration.includedStatuses.includes(
                            status,
                          )}
                          onChange={() => toggleStatus(status)}
                        />
                        <span>{status}</span>
                      </label>
                    ))
                  ) : (
                    <span className="finance-reporting-field__hint">
                      No status values in the current data.
                    </span>
                  )}
                </div>
              </fieldset>
            )}
          </div>

          <div className="finance-reporting-section">
            <div className="finance-reporting-section__heading">
              <span className="finance-reporting-section__number">04</span>
              <div>
                <h4>Dimensions</h4>
                <p>Identifiers and fields used for drill-down.</p>
              </div>
            </div>
            <FieldSelect
              label="Client"
              fieldRole="dimension"
              fields={fields}
              value={configuration.mappings.client}
              onChange={(uuid) => onMappingChange("client", uuid)}
            />
            <FieldSelect
              label={managerLabel}
              fieldRole="dimension"
              fields={fields}
              value={configuration.mappings.manager}
              onChange={(uuid) => onMappingChange("manager", uuid)}
            />
            <label className="finance-reporting-field">
              <span className="finance-reporting-field__label">
                {managerLabel} label
              </span>
              <input
                className="finance-reporting-field__control"
                type="text"
                placeholder={mappingLabels.manager}
                value={configuration.roleLabels.manager ?? ""}
                onChange={(event) =>
                  onChange({
                    roleLabels: {
                      ...configuration.roleLabels,
                      manager: event.target.value,
                    },
                  })
                }
              />
            </label>
            <FieldSelect
              label={marketLabel}
              fieldRole="dimension"
              fields={fields}
              value={configuration.mappings.market}
              onChange={(uuid) => onMappingChange("market", uuid)}
            />
            <FieldSelect
              label="Campaign ID"
              fieldRole="identity"
              fields={fields}
              value={configuration.mappings.campaign}
              onChange={(uuid) => onMappingChange("campaign", uuid)}
            />
            <FieldSelect
              label="Campaign name"
              fieldRole="dimension"
              fields={fields}
              value={configuration.mappings.campaignName}
              onChange={(uuid) => onMappingChange("campaignName", uuid)}
            />
            <FieldSelect
              label="Creator ID"
              fieldRole="identity"
              fields={fields}
              value={configuration.mappings.creator}
              onChange={(uuid) => onMappingChange("creator", uuid)}
            />
            <FieldSelect
              label={creatorNameLabel}
              fieldRole="dimension"
              fields={fields}
              value={configuration.mappings.creatorName}
              onChange={(uuid) => onMappingChange("creatorName", uuid)}
            />
            <label className="finance-reporting-field">
              <span className="finance-reporting-field__label">
                {creatorNameLabel} label
              </span>
              <input
                className="finance-reporting-field__control"
                type="text"
                placeholder={mappingLabels.creatorName}
                value={configuration.roleLabels.creatorName ?? ""}
                onChange={(event) =>
                  onChange({
                    roleLabels: {
                      ...configuration.roleLabels,
                      creatorName: event.target.value,
                    },
                  })
                }
              />
            </label>
            <FieldSelect
              label="Cost type"
              fieldRole="dimension"
              fields={fields}
              value={configuration.mappings.costType}
              onChange={(uuid) => onMappingChange("costType", uuid)}
            />
            <fieldset className="finance-reporting-fieldset">
              <legend>{creatorNameLabel} fallback</legend>
              <p className="finance-reporting-field__hint">
                When {creatorNameLabel.toLowerCase()} is empty, the first
                matching rule below supplies a label (e.g. &quot;Boosting&quot;)
                instead of &quot;Unassigned&quot;, based on the Cost type field
                above. Matching is case-insensitive. Leave empty for no
                fallback.
              </p>
              {configuration.creatorFallbackRules.map((rule, index) => (
                <div
                  key={index}
                  style={{ display: "flex", gap: 8, alignItems: "center" }}
                >
                  <input
                    className="finance-reporting-field__control"
                    type="text"
                    placeholder="Cost type value, e.g. Boosting"
                    value={rule.costTypeValue}
                    onChange={(event) =>
                      updateCreatorFallbackRule(index, {
                        costTypeValue: event.target.value,
                      })
                    }
                  />
                  <input
                    className="finance-reporting-field__control"
                    type="text"
                    placeholder="Label, e.g. Boosting"
                    value={rule.label}
                    onChange={(event) =>
                      updateCreatorFallbackRule(index, {
                        label: event.target.value,
                      })
                    }
                  />
                  <button
                    type="button"
                    className="finance-reporting-button finance-reporting-button--secondary"
                    onClick={() => removeCreatorFallbackRule(index)}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="finance-reporting-button finance-reporting-button--secondary"
                onClick={addCreatorFallbackRule}
              >
                Add fallback rule
              </button>
            </fieldset>
            <p className="finance-reporting-field__hint">
              Additional dimensions combine selected member fields. A record
              contributes once per distinct member value.
            </p>
            {[0, 1].map((index) => {
              const key =
                `customDimension${index + 1}` as AdditionalDimension["key"];
              const dimension = configuration.additionalDimensions.find(
                (item) => item.key === key,
              ) ?? {
                key,
                label: "",
                fieldUuids: [],
              };
              return (
                <fieldset
                  className="finance-reporting-fieldset"
                  key={dimension.key}
                >
                  <legend>Additional dimension {index + 1}</legend>
                  <label className="finance-reporting-field">
                    <span className="finance-reporting-field__label">
                      Label
                    </span>
                    <input
                      className="finance-reporting-field__control"
                      type="text"
                      value={dimension.label}
                      onChange={(event) =>
                        updateAdditionalDimension(index, {
                          label: event.target.value,
                        })
                      }
                    />
                  </label>
                  <label className="finance-reporting-field">
                    <span className="finance-reporting-field__label">
                      Member fields
                    </span>
                    <select
                      multiple
                      className="finance-reporting-field__control"
                      value={dimension.fieldUuids}
                      onChange={(event) =>
                        updateAdditionalDimension(index, {
                          fieldUuids: Array.from(
                            event.target.selectedOptions,
                          ).map((option) => option.value),
                        })
                      }
                    >
                      {memberFields.map((field) => (
                        <option key={field.uuid} value={field.uuid}>
                          {field.name.trim()} - {field.scope}
                        </option>
                      ))}
                    </select>
                  </label>
                </fieldset>
              );
            })}
          </div>

          <div className="finance-reporting-section">
            <div className="finance-reporting-section__heading">
              <span className="finance-reporting-section__number">05</span>
              <div>
                <h4>Revenue &amp; profit targets</h4>
                <p>
                  Per-market, per-month targets. Optional - leave empty for a
                  workspace that doesn&apos;t use them.
                </p>
              </div>
            </div>

            {configuration.targets.length > 0 && (
              <p className="finance-reporting-field__hint">
                {configuration.targets.length} target
                {configuration.targets.length === 1 ? "" : "s"} configured
                across {targetMarketCount} market
                {targetMarketCount === 1 ? "" : "s"}.
              </p>
            )}

            <label className="finance-reporting-field">
              <span className="finance-reporting-field__label">
                Template year
              </span>
              <input
                className="finance-reporting-field__control"
                type="number"
                style={{ maxWidth: 120 }}
                value={targetsYear}
                onChange={(event) =>
                  setTargetsYear(Number(event.target.value) || targetsYear)
                }
              />
            </label>
            <button
              type="button"
              className="finance-reporting-button finance-reporting-button--secondary"
              onClick={copyTargetsTemplate}
            >
              Copy blank template for {targetsYear}
            </button>
            <p className="finance-reporting-field__hint">
              Copies a tab-separated template - one row per market already in
              this workspace&apos;s data, a revenue and a profit column per
              month - to your clipboard and into the box below. Fill it in a
              spreadsheet (delete rows for markets that don&apos;t need a
              target), then paste the result back in.
            </p>

            <label className="finance-reporting-field">
              <span className="finance-reporting-field__label">
                Paste targets (TSV)
              </span>
              <textarea
                className="finance-reporting-field__control"
                rows={6}
                value={targetsTsv}
                onChange={(event) => setTargetsTsv(event.target.value)}
                placeholder={"Market\tJan-26_revenue\tJan-26_profit\t..."}
              />
            </label>
            <button
              type="button"
              className="finance-reporting-button finance-reporting-button--secondary"
              onClick={importTargetsTsv}
              disabled={!targetsTsv.trim()}
            >
              Replace targets from pasted TSV
            </button>
            <p className="finance-reporting-field__hint">
              Replaces every currently configured target with what&apos;s pasted
              above. Like the rest of this panel, nothing reaches the workspace
              until you store the configuration below.
            </p>
            {targetsImportErrors.length > 0 && (
              <div className="finance-reporting-callout">
                <strong>Some rows could not be read</strong>
                <span>{targetsImportErrors.join(" ")}</span>
              </div>
            )}

            {configuration.targets.length > 0 && (
              <button
                type="button"
                className="finance-reporting-button finance-reporting-button--secondary"
                onClick={() => onChange({ targets: [] })}
              >
                Clear all targets
              </button>
            )}
          </div>

          <div className="finance-reporting-group__actions">
            <span className="finance-reporting-group__state">
              {renderSavedState()}
            </span>
            <div className="finance-reporting-group__buttons">
              <button
                type="button"
                className="finance-reporting-button finance-reporting-button--secondary"
                onClick={onReset}
                disabled={isSaving}
              >
                Reset
              </button>
              <button
                type="button"
                className="finance-reporting-button finance-reporting-button--primary"
                onClick={onSave}
                disabled={!canSave || isSaving}
              >
                {isSaving ? "Storing..." : "Store configuration"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
